import {useCallback, useEffect, useRef, useState} from 'react';
import {useLocation} from 'react-router-dom';
import {
    useIdentifyHubspotUserMutation,
    type VwpHubspotIdentifyUserResponse
} from "@/store/generated/api.ts";

import {useAuth} from "@/hooks/useAuth";
import {syncHubspotTrackingParamsFromUrl} from '@/utils/hubspotTracking';

export const useHubspotChatIntegration = () => {
    const [isReady, setIsReady] = useState(false);
    const isChatInitialized = useRef(false);
    const isScriptLoaded = useRef(false);
    const [identifyUserForHubspot] = useIdentifyHubspotUserMutation();
    const {isLoading: isAuthLoading, isAuthenticated, user} = useAuth();
    const location = useLocation();

    const initializeChat = useCallback(async (user) => {
        if (isChatInitialized.current) {
            return; // Chat had been injected and initialized before. Do nothing.
        }

        // Persist tracker params before any HubSpot script initialization happens.
        syncHubspotTrackingParamsFromUrl();
        isChatInitialized.current = true;

        const hubspotId = (window as any).ENV?.VITE_HUBSPOT_CHAT_ID || import.meta.env.VITE_HUBSPOT_CHAT_ID;

        if (hubspotId) {
            // 1. get a Hubspot session ID for the current user so that hubspot can associate a chat session with the right contact
            let hubspotSessionInfo: VwpHubspotIdentifyUserResponse = {}
            try {
                hubspotSessionInfo = await identifyUserForHubspot().unwrap();
            } catch (error) {
                // hubspot session could not be initialized.
                // Continue performing the chat initialization without having a user context.
                console.error("Failed to initialize HubSpot:", error);
            }

            // Initialize the _hsq queue for analytics tracking before script loads
            window._hsq = window._hsq || [];

            // Queue identify call with user information for analytics tracking
            window._hsq.push(['identify', {
                email: user.email,
                firstName: user.firstName,
                lastName: user.lastName,
            }]);

            // Queue initial page view with current path
            window._hsq.push(['setPath', location.pathname]);
            window._hsq.push(['trackPageView']);

            // Set global hubspot chat settings
            (window as any).hsConversationsSettings = {
                enableWidgetCookieBanner: true,
                identificationToken: hubspotSessionInfo.identificationToken,
                identificationEmail: hubspotSessionInfo.identificationEmail || user.email,
            };

            // Inject the hubspot script (queued analytics will be flushed when script loads)
            const script = document.createElement('script');
            script.type = 'text/javascript';
            script.id = 'hs-script-loader';
            script.async = true;
            script.defer = true;
            script.src = `//js-eu1.hs-scripts.com/${hubspotId}.js`;
            script.onload = () => {
                isScriptLoaded.current = true;
            };

            document.body.appendChild(script);
            setIsReady(true);
        }
    }, [isChatInitialized, identifyUserForHubspot]);

    useEffect(() => {
        if (isAuthenticated && !isAuthLoading) {
            initializeChat(user);
        }
    }, [identifyUserForHubspot, isAuthLoading, isAuthenticated, user, initializeChat]);

    // Track page views on route changes
    useEffect(() => {
        if (isScriptLoaded.current && window._hsq) {
            window._hsq.push(['setPath', location.pathname]);
            window._hsq.push(['trackPageView']);
        }
    }, [location.pathname]);

    return {isReady};
};